import { useCallback, useEffect, useRef, useState } from 'react';

import { useRouter } from 'expo-router';
import { useStoreContext } from 'features/store';
import { useSelectStore } from 'features/store/hooks/use-select-store';
import { useNearbyRestaurants } from 'features/store/pages/pickup-store-selector/hooks/helpers/use-restaurants';

import { ILocation } from '@rbi-ctg/frontend';
import { AnimatedSplashScreen } from 'components/splash-screen/splash-screen';
import { toaster } from 'components/toaster/toaster';
import { useDistanceText } from 'hooks/geolocation/use-distance-text';
import { useRoute } from 'hooks/navigation/use-route';
import { useUpdateOnAppStateChange } from 'hooks/use-update-on-app-state-change';
import { routes } from 'navigation/routing';
import {
  isFavoritesRoute,
  isItemPickerOrComboRoute,
  isRecentItemsRoute,
  isSectionRoute,
} from 'navigation/routing/utils';
import { useGeolocation } from 'state/geolocation';
import { actions, useAppDispatch } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { watchUserPositionTemporarily } from 'utils/geolocation';
import { logger } from 'utils/logger';

import {
  IUseAuoSelectClosestStorePrompt,
  UseAuoSelectClosestStorePromptReturnType,
} from '../types';
import {
  COLD_START_LOCATION_WATCH_MS,
  getCloserStoreCooldownRemainingMs,
  hasMovedSignificantly,
  isEligibleRoute,
  isTimestampExpired,
} from '../utils';

import { useAutoSelectPromptState } from './use-auto-select-prompt-state';
import { useNearbyStoreData } from './use-nearby-store-data';

type EvaluateCloserRestaurantOptions = {
  coordinates?: ILocation;
  /** Skip the significant-move gate (cold start, resume). */
  force?: boolean;
};

export const useAuoSelectClosestStorePrompt = ({
  userCoordinates,
  activeStoreId,
}: IUseAuoSelectClosestStorePrompt): UseAuoSelectClosestStorePromptReturnType => {
  const dispatch = useAppDispatch();
  const { pathname } = useRoute();
  const { navigate } = useRouter();
  const { fetchingPosData } = useOrderContext();
  const { loading } = useNearbyRestaurants();
  const { noStoreSelected } = useStoreContext();
  const { loadCurrentPositionAsync, setUserCoordinates } = useGeolocation();
  const autoSelectStorePromptTimeout = useFlag(LaunchDarklyFlag.AUTO_SELECT_STORE_PROMPT_TIMEOUT);
  const { nearbyStoreData, setNearbyStoreData, fetchNearbyRestaurant } = useNearbyStoreData({
    userCoordinates,
  });
  const selectStore = useSelectStore();
  const { isOpen, setIsOpen, updateAutoSelectTimestamp } = useAutoSelectPromptState();

  const { distanceText } = useDistanceText({
    restaurant: nearbyStoreData?.restaurant || null,
    shouldUseUserCoordinatesIfAvailable: true,
  });

  const hasHiddenSplashRef = useRef(false);
  const lastCheckedCoordsRef = useRef<ILocation | null>(null);
  const stopLocationRefinementRef = useRef<(() => void) | null>(null);
  const locationRefinementSessionRef = useRef(0);
  const skipNextCoordsEffectRef = useRef(true);
  const [cooldownGeneration, setCooldownGeneration] = useState(0);
  const evaluateCloserRestaurantRef = useRef(
    async (_options?: EvaluateCloserRestaurantOptions) => {}
  );

  const stopLocationRefinement = useCallback(() => {
    // Invalidate any in-flight watchUserPositionTemporarily setup.
    locationRefinementSessionRef.current += 1;
    stopLocationRefinementRef.current?.();
    stopLocationRefinementRef.current = null;
  }, []);

  const beginDrawerCooldown = useCallback(() => {
    updateAutoSelectTimestamp();
    setCooldownGeneration(generation => generation + 1);
  }, [updateAutoSelectTimestamp]);

  const evaluateCloserRestaurant = useCallback(
    async (options: EvaluateCloserRestaurantOptions = {}) => {
      if (noStoreSelected || !isTimestampExpired(autoSelectStorePromptTimeout)) {
        return;
      }

      let location = options.coordinates;

      if (!location) {
        try {
          location = (await loadCurrentPositionAsync()) ?? userCoordinates;
        } catch (error) {
          logger.warn({
            message: 'Failed to refresh location for closer store prompt; using known coordinates',
            error,
          });
          location = userCoordinates;
        }
      }

      if (
        !options.force &&
        lastCheckedCoordsRef.current &&
        !hasMovedSignificantly(lastCheckedCoordsRef.current, location)
      ) {
        return;
      }

      await fetchNearbyRestaurant(location);
      lastCheckedCoordsRef.current = location;
    },
    [
      noStoreSelected,
      autoSelectStorePromptTimeout,
      loadCurrentPositionAsync,
      fetchNearbyRestaurant,
      userCoordinates,
    ]
  );

  evaluateCloserRestaurantRef.current = evaluateCloserRestaurant;

  const startLocationRefinement = useCallback(async () => {
    if (!isTimestampExpired(autoSelectStorePromptTimeout)) {
      return;
    }

    stopLocationRefinement();
    const sessionId = locationRefinementSessionRef.current;

    try {
      const stop = await watchUserPositionTemporarily({
        durationMs: COLD_START_LOCATION_WATCH_MS,
        onUpdate: location => {
          setUserCoordinates(location);
          void evaluateCloserRestaurantRef.current({ coordinates: location });
        },
      });

      if (sessionId !== locationRefinementSessionRef.current) {
        stop();
        return;
      }

      stopLocationRefinementRef.current = stop;
    } catch (error) {
      logger.warn({
        message: 'Failed to start closer-store cold-start location watch',
        error,
      });
    }
  }, [autoSelectStorePromptTimeout, setUserCoordinates, stopLocationRefinement]);

  useUpdateOnAppStateChange(async appState => {
    if (appState === 'background' || appState === 'inactive') {
      stopLocationRefinement();
      setNearbyStoreData(null);
    } else if (appState === 'active') {
      await evaluateCloserRestaurantRef.current({ force: true });
      await startLocationRefinement();
    }
  });

  const handleCloseAutoSelectPrompt = useCallback(() => {
    beginDrawerCooldown();
    setNearbyStoreData(null);
    setIsOpen(false);
  }, [beginDrawerCooldown, setIsOpen, setNearbyStoreData]);

  const onRestaurantConfirmed = useCallback(() => {
    setNearbyStoreData(null);
    setIsOpen(false);

    if (
      isFavoritesRoute(pathname) ||
      isSectionRoute(pathname) ||
      isRecentItemsRoute(pathname) ||
      isItemPickerOrComboRoute(pathname)
    ) {
      navigate(routes.menu);
    }

    toaster.positive(t('weHaveSelectedClosestStore'));
  }, [navigate, setIsOpen, setNearbyStoreData, pathname]);

  // Re-evaluate when cooldown elapses (same session, or remaining time after remount).
  useEffect(() => {
    const remainingMs = getCloserStoreCooldownRemainingMs(autoSelectStorePromptTimeout);
    if (remainingMs <= 0) {
      return;
    }

    const timeoutId = setTimeout(() => {
      void (async () => {
        await evaluateCloserRestaurantRef.current({ force: true });
        await startLocationRefinement();
      })();
    }, remainingMs);

    return () => clearTimeout(timeoutId);
  }, [cooldownGeneration, autoSelectStorePromptTimeout, startLocationRefinement]);

  const confirmRestaurant = useCallback(async () => {
    if (!nearbyStoreData?.restaurant) {
      return;
    }

    beginDrawerCooldown();
    dispatch(actions.loyalty.setShouldRefetchOffers(true));
    await selectStore({
      newStore: nearbyStoreData.restaurant,
      callback: onRestaurantConfirmed,
      skipChangeStoreMessage: true,
    });
  }, [beginDrawerCooldown, dispatch, nearbyStoreData, onRestaurantConfirmed, selectStore]);

  useEffect(() => {
    if (isOpen || !isTimestampExpired(autoSelectStorePromptTimeout)) {
      return;
    }

    if (!nearbyStoreData?.restaurant?._id || nearbyStoreData.restaurant._id === activeStoreId) {
      return;
    }

    setIsOpen(true);
  }, [nearbyStoreData, isOpen, setIsOpen, activeStoreId, autoSelectStorePromptTimeout]);

  useEffect(() => {
    if (isOpen && !nearbyStoreData?.restaurant?._id) {
      setIsOpen(false);
    }
  }, [isOpen, nearbyStoreData, setIsOpen]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!hasHiddenSplashRef.current) {
        await AnimatedSplashScreen.hide();
        hasHiddenSplashRef.current = true;
      }
      if (cancelled) {
        return;
      }

      // Cooldown timeout effect handles deferred eval when still cooling down.
      if (!isTimestampExpired(autoSelectStorePromptTimeout)) {
        return;
      }

      await evaluateCloserRestaurantRef.current({ force: true });
      if (cancelled) {
        return;
      }
      await startLocationRefinement();
    };

    void run();

    return () => {
      cancelled = true;
      stopLocationRefinement();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only cold start + refinement
  }, []);

  useEffect(() => {
    if (skipNextCoordsEffectRef.current) {
      skipNextCoordsEffectRef.current = false;
      return;
    }

    void evaluateCloserRestaurantRef.current({
      coordinates: userCoordinates,
    });
  }, [userCoordinates.lat, userCoordinates.lng]);

  useEffect(() => {
    if (!activeStoreId) {
      return;
    }

    void evaluateCloserRestaurantRef.current({ force: true });
  }, [activeStoreId]);

  useEffect(() => {
    if (!isEligibleRoute(pathname)) {
      return;
    }

    void evaluateCloserRestaurantRef.current({ force: true });
  }, [pathname]);

  return {
    nearbyStoreData,
    distanceText,
    isLoading: loading || fetchingPosData,
    confirmRestaurant,
    isOpen,
    handleCloseAutoSelectPrompt,
  };
};
